import React, { useEffect, useState } from "react";
import { DeskPanel } from "../desktop/DeskTable";
import {
  ESTIMATE_TAX_TYPE_OPTIONS,
  applyTaxTypeSelection,
  formatStandardTaxRateDisplay,
  getTaxLineLabel,
  resolveTaxLineRate,
} from "../../helpers/estimateTax";
import { formatCurrency } from "../../helpers/estimateTotals";
import "./invoiceDesktop.css";

function slotAmount(line, summaryLines, used) {
  if (!line || line.type === "none") return 0;
  const rate = Number(resolveTaxLineRate(line));
  const label = getTaxLineLabel(line);
  const index = (summaryLines || []).findIndex(
    (row, i) => !used.has(i) && row.label === label && Number(row.rate) === rate,
  );
  if (index < 0) return 0;
  used.add(index);
  return summaryLines[index].amount || 0;
}

function TaxSlot({ label, line, onChange, amount, disabled }) {
  const standard = line.type !== "custom";
  return (
    <div className="inv-rcpt__tax">
      <span>{label}</span>
      <select
        aria-label={`${label} type`}
        value={line.type}
        disabled={disabled}
        onChange={(event) => onChange(applyTaxTypeSelection(line, event.target.value))}
      >
        {ESTIMATE_TAX_TYPE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <input
        aria-label={`${label} rate`}
        className="inv-rcpt__input"
        inputMode="decimal"
        value={standard ? formatStandardTaxRateDisplay(line.type) : line.rate}
        readOnly={standard}
        disabled={disabled || standard}
        onChange={(event) => onChange({ ...line, rate: event.target.value })}
      />
      <span className="inv-rcpt__amt">{formatCurrency(amount)}</span>
      {line.type === "custom" ? (
        <input
          aria-label={`${label} name`}
          className="inv-rcpt__input inv-rcpt__input--text inv-rcpt__name"
          placeholder="Tax name"
          value={line.customName}
          disabled={disabled}
          onChange={(event) => onChange({ ...line, customName: event.target.value })}
        />
      ) : null}
    </div>
  );
}

/**
 * Desktop invoice summary. Same fields and actions as the review form,
 * laid out like the estimate pricing receipt.
 */
export function InvoiceReviewReceipt({
  rollups,
  showServices = false,
  projectDiscountPercent,
  onProjectDiscount,
  canEditDiscount,
  goodwillAdjustment,
  onGoodwill,
  canEditGoodwill,
  reviewNotes,
  onReviewNotes,
  canEditNotes,
  taxLine1,
  taxLine2,
  onTax1,
  onTax2,
  canEditTax,
  saving = false,
  deleting = false,
  onSave,
  canReseed = false,
  onReseed,
  canPreview = false,
  onPreview,
  canSend = false,
  onSend,
}) {
  const [showTax2, setShowTax2] = useState(() => Boolean(taxLine2?.type && taxLine2.type !== "none"));
  useEffect(() => {
    if (taxLine2?.type && taxLine2.type !== "none") setShowTax2(true);
  }, [taxLine2?.type]);

  const used = new Set();
  const summaryLines = rollups?.taxSummary?.lines || [];
  const tax1Amount = slotAmount(taxLine1, summaryLines, used);
  const tax2Amount = slotAmount(taxLine2, summaryLines, used);
  const taxDisabled = !canEditTax || saving;

  return (
    <DeskPanel title="Summary" className="inv-pricing-panel">
      <div className="inv-rcpt" data-testid="invoice-review-receipt">
        <div className="inv-rcpt__row">
          <span>Labour</span>
          <span>{formatCurrency(rollups?.subtotalLabour)}</span>
        </div>
        <div className="inv-rcpt__row">
          <span>Materials</span>
          <span>{formatCurrency(rollups?.subtotalMaterials)}</span>
        </div>
        {showServices ? (
          <div className="inv-rcpt__row">
            <span>Services</span>
            <span>{formatCurrency(rollups?.subtotalServices)}</span>
          </div>
        ) : null}
        <div className="inv-rcpt__row">
          <span>Line subtotal</span>
          <span>{formatCurrency(rollups?.lineSubtotal)}</span>
        </div>

        <div className="inv-rcpt__field">
          <span>Discount %</span>
          <input
            aria-label="Project discount %"
            className="inv-rcpt__input"
            inputMode="decimal"
            placeholder="0"
            value={projectDiscountPercent}
            disabled={!canEditDiscount || saving}
            onChange={(event) => onProjectDiscount(event.target.value)}
          />
        </div>
        <div className="inv-rcpt__field">
          <span>Goodwill</span>
          <input
            aria-label="Goodwill adjustment"
            className="inv-rcpt__input"
            inputMode="decimal"
            placeholder="0.00"
            value={goodwillAdjustment}
            disabled={!canEditGoodwill || saving}
            onChange={(event) => onGoodwill(event.target.value)}
          />
        </div>

        <div className="inv-rcpt__row is-mid">
          <span>Subtotal</span>
          <span>{formatCurrency(rollups?.subtotal)}</span>
        </div>

        <TaxSlot
          label="Tax 1"
          line={taxLine1}
          onChange={onTax1}
          amount={tax1Amount}
          disabled={taxDisabled}
        />
        {showTax2 ? (
          <div>
            <TaxSlot
              label="Tax 2"
              line={taxLine2}
              onChange={onTax2}
              amount={tax2Amount}
              disabled={taxDisabled}
            />
            {!taxDisabled ? (
              <button
                type="button"
                className="inv-rcpt__link"
                onClick={() => {
                  onTax2(applyTaxTypeSelection(taxLine2, "none"));
                  setShowTax2(false);
                }}
              >
                Remove tax 2
              </button>
            ) : null}
          </div>
        ) : (
          !taxDisabled ? (
            <button type="button" className="inv-rcpt__link" onClick={() => setShowTax2(true)}>
              Add tax 2
            </button>
          ) : null
        )}

        <div className="inv-rcpt__row is-total">
          <span>Grand Total</span>
          <span>{formatCurrency(rollups?.totalAmount)}</span>
        </div>

        <label className="inv-rcpt__notes">
          <span>Internal review notes</span>
          <input
            aria-label="Internal review notes"
            className="inv-rcpt__input inv-rcpt__input--text"
            placeholder="Manager notes (not on client PDF)"
            value={reviewNotes}
            disabled={!canEditNotes || saving}
            onChange={(event) => onReviewNotes(event.target.value)}
          />
        </label>

        <button
          type="button"
          className="dt-btn dt-btn--primary inv-rcpt__save"
          disabled={saving}
          onClick={onSave}
        >
          {saving ? "Saving…" : "Save Draft"}
        </button>

        {canReseed ? (
          <div className="inv-rcpt__action">
            <button type="button" className="dt-btn inv-rcpt__save" disabled={saving} onClick={onReseed}>
              Refresh from Project
            </button>
            <p className="inv-rcpt__help">
              Imports any uninvoiced labour and materials from the project into this invoice.
            </p>
          </div>
        ) : null}

        {canPreview ? (
          <div className="inv-rcpt__action">
            <button type="button" className="dt-btn inv-rcpt__save" onClick={onPreview}>
              Preview Invoice
            </button>
            <p className="inv-rcpt__help">Review the invoice layout and export a PDF when ready.</p>
          </div>
        ) : null}
        {canSend ? (
          <div className="inv-rcpt__action">
            <button
              type="button"
              className="dt-btn inv-rcpt__save"
              disabled={saving || deleting}
              onClick={onSend}
            >
              Send Invoice
            </button>
            <p className="inv-rcpt__help">Finalizes the invoice and records project allocations.</p>
          </div>
        ) : null}
      </div>
    </DeskPanel>
  );
}
